import * as React from "react";

type LoginAccountContextType = {
  accountNotLinked: boolean | null;
  googleEmail: string | null;
  setAccountNotLinked: React.Dispatch<React.SetStateAction<boolean | null>>;
  setGoogleEmail: React.Dispatch<React.SetStateAction<string | null>>;
};

const LoginContext = React.createContext<LoginAccountContextType | undefined>(
  undefined
);

export const useLoginContext = (): LoginAccountContextType => {
  const context = React.useContext(LoginContext);
  if (!context) {
    throw new Error(
      "useLoginContext must be used within a LoginContextProvider"
    );
  }
  return context;
};

interface LoginContextProviderProps {
  children: React.ReactNode;
}

// TODO we should probably use a loader in LS2Pac, at the top of the route tree to strip google sso params, set in memory, and redirect
// we should also set them to null as soon as we login or cancel login when unlinked
export const LoginContextProvider: React.FC<LoginContextProviderProps> = ({
  children,
}) => {
  const [accountNotLinked, setAccountNotLinked] = React.useState<
    boolean | null
  >(null);
  const [googleEmail, setGoogleEmail] = React.useState<string | null>(null);

  // Extract google query parameters from the URL search or hash
  const getParams = ({ hash, search }: Location) => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has("accountNotLinked")) return searchParams;

    // param not found in search, checking hash...

    if (hash.includes("?")) {
      // Examples
      // React Router HashRouter format: /#/login?accountNotLinked=true&googleEmail=ryan.neuffer%40gmail.com&from=%2Fmyaccount%2Fsummary
      // LS2Kids /kids#/categorySearch?id=6246613&accountNotLinked=true&googleEmail=ryan.neuffer%40gmail.com
      const [path, query] = hash.split("?");
      return new URLSearchParams(query);
    }

    // Examples
    // LS2Pac Legacy URL format: /#section=search&term=harry%20potter&page=0&sortKey=Relevancy&db=ls2pac&branchFilters=%5B%5D&facetFilters=%5B%5D&accountNotLinked=true&googleEmail=ryan.neuffer%40gmail.com
    return new URLSearchParams(hash.slice(1)); // remove the #
  };

  React.useEffect(() => {
    const params = getParams(window.location);
    if (params.get("accountNotLinked") === "true") {
      setAccountNotLinked(true);
      setGoogleEmail(params.get("googleEmail"));
    } else {
      setAccountNotLinked(false);
    }
  }, [window.location]);

  return (
    <LoginContext.Provider
      value={{
        accountNotLinked,
        googleEmail,
        setAccountNotLinked,
        setGoogleEmail,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};
